import { useState } from 'react';
import { comics, type Comic } from '@/mocks/comics';
import ComicFilters from './ComicFilters';

export default function ComicsGrid() {
  const [category, setCategory] = useState('全部');
  const [status, setStatus] = useState('全部');
  const [keyword, setKeyword] = useState('');
  const [saved, setSaved] = useState<string[]>(['jianyu-xingchen', 'mowang-daren']);

  const filtered = comics.filter((c) => {
    const matchCategory = category === '全部' || c.category === category;
    const matchStatus = status === '全部' || c.status === status;
    const kw = keyword.trim().toLowerCase();
    const matchKeyword = !kw || c.title.toLowerCase().includes(kw) || c.author.toLowerCase().includes(kw);
    return matchCategory && matchStatus && matchKeyword;
  });

  const toggleSave = (id: string) => {
    setSaved((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
      <ComicFilters
        category={category}
        setCategory={setCategory}
        status={status}
        setStatus={setStatus}
        keyword={keyword}
        setKeyword={setKeyword}
      />

      {filtered.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-background-200/70 bg-background-50 py-16 text-center">
          <i className="ri-inbox-line text-4xl text-foreground-300"></i>
          <p className="mt-4 text-foreground-600">没有找到匹配的漫画，换个关键词试试吧。</p>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((c) => (
            <ComicCard key={c.id} comic={c} saved={saved.includes(c.id)} onToggle={() => toggleSave(c.id)} />
          ))}
        </div>
      )}
    </section>
  );
}

function ComicCard({ comic, saved, onToggle }: { comic: Comic; saved: boolean; onToggle: () => void }) {
  return (
    <article className="group overflow-hidden rounded-2xl border border-background-200/70 bg-background-50 transition-transform duration-300 hover:-translate-y-1">
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={comic.cover}
          alt={`${comic.title} 漫画封面`}
          title={`${comic.title} ${comic.category} 漫画`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-background-950/70 px-3 py-1 text-xs font-medium text-background-50 backdrop-blur-sm">
          {comic.category}
        </span>
        <span
          className={`absolute right-3 top-3 rounded-full px-3 py-1 text-xs font-medium ${
            comic.status === '连载中' ? 'bg-primary-500 text-background-50' : 'bg-accent-500 text-foreground-950'
          }`}
        >
          {comic.status}
        </span>
        <button
          type="button"
          onClick={onToggle}
          aria-label={saved ? `取消收藏 ${comic.title}` : `收藏 ${comic.title}`}
          className={`absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full text-lg transition-colors cursor-pointer ${
            saved
              ? 'bg-primary-500 text-background-50'
              : 'bg-background-50/90 text-foreground-700 hover:bg-primary-500 hover:text-background-50'
          }`}
        >
          <i className={saved ? 'ri-bookmark-fill' : 'ri-bookmark-line'}></i>
        </button>
      </div>
      <div className="p-4">
        <h3 className="text-base font-bold text-foreground-950">{comic.title}</h3>
        <p className="mt-0.5 text-xs text-foreground-500">{comic.author}</p>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {comic.tags.map((t) => (
            <span key={t} className="rounded-full bg-background-100 px-2.5 py-0.5 text-[11px] text-foreground-600">
              {t}
            </span>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-background-100 pt-3">
          <span className="text-xs text-foreground-600">{comic.latestChapter}</span>
          <span className="flex items-center gap-1 text-xs font-semibold text-accent-700">
            <i className="ri-star-fill"></i>
            {comic.rating}
          </span>
        </div>
      </div>
    </article>
  );
}